class CtmRoomPreview extends HTMLElement {
  static desktopBreakpoint = 1024;

  constructor() {
    super();
    this.slideCount = Number(this.dataset.slideCount || 0);
    this.desktop = null;
    this.mobileTrack = null;
    this.panels = [];
    this.copySlides = [];
    this.mobileSlides = [];
    this.dots = [];
    this.mobileCarousel = null;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeIndex = 0;
    this.ticking = false;
    this.currentScrollTop = null;
    this.scrollDirection = Number.parseInt(getComputedStyle(this).getPropertyValue('--ctm-roomprev-scroll-dir'), 10) || 1;
    this.boundRequestScrollUpdate = this.requestScrollUpdate.bind(this);
    this.boundResize = this.handleResize.bind(this);
    this.boundSectionLoad = this.handleSectionLoad.bind(this);
  }

  connectedCallback() {
    this.desktop = this.querySelector('[data-room-preview-desktop]');
    this.mobileTrack = this.querySelector('[data-room-preview-mobile-track]');
    this.panels = Array.from(this.querySelectorAll('[data-room-preview-panel]'));
    this.copySlides = Array.from(this.querySelectorAll('[data-room-preview-copy]'));
    this.mobileSlides = Array.from(this.querySelectorAll('[data-room-preview-mobile-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-room-preview-dot]'));

    this.initDotEvents();
    this.initMobileCarousel();
    this.initImageLoadEvents();
    this.initScrollEvents();
    this.setMediaHeight();
    this.resetPanelClips();
    this.setActive(0);
    this.requestScrollUpdate();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.boundRequestScrollUpdate);
    window.removeEventListener('resize', this.boundResize);
    document.removeEventListener('shopify:section:load', this.boundSectionLoad);
    this.destroyMobileCarousel();
  }

  initDotEvents() {
    this.dots
      .filter((dot) => dot.closest('[data-room-preview-desktop]'))
      .forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.index || 0);
          this.setActive(index, { scrollDesktop: window.innerWidth >= CtmRoomPreview.desktopBreakpoint });
        });
      });
  }

  initMobileCarousel() {
    if (!this.mobileTrack || this.slideCount <= 1 || typeof Flickity === 'undefined') return;

    this.mobileCarousel = new Flickity(this.mobileTrack, {
      accessibility: false,
      adaptiveHeight: true,
      cellAlign: 'left',
      contain: true,
      pageDots: false,
      prevNextButtons: false,
      rightToLeft: theme.config.rtl,
      wrapAround: false,
    });

    this.mobileCarousel.on('change', (index) => {
      this.setActiveState(index);
      this.dispatchEvent(new CustomEvent('slider:change', {
        detail: {
          currentPage: index + 1,
          currentElement: this.mobileSlides[index],
        },
      }));
    });

    this.setActiveState(this.mobileCarousel.selectedIndex || 0);
    window.requestAnimationFrame(() => this.mobileCarousel?.resize());
  }

  destroyMobileCarousel() {
    if (!this.mobileCarousel) return;
    this.mobileCarousel.destroy();
    this.mobileCarousel = null;
  }

  select(selectedIndex, immediate = false) {
    if (!this.mobileCarousel) return;
    this.mobileCarousel.select(Math.max(selectedIndex - 1, 0), false, immediate);
  }

  initImageLoadEvents() {
    this.querySelectorAll('.ctm-roomprev_img').forEach((image) => {
      if (image.complete) return;
      image.addEventListener('load', () => {
        this.setMediaHeight();
        this.requestScrollUpdate();
      }, { once: true });
    });
  }

  initScrollEvents() {
    window.addEventListener('scroll', this.boundRequestScrollUpdate, { passive: true });
    window.addEventListener('resize', this.boundResize);
    document.addEventListener('shopify:section:load', this.boundSectionLoad);
  }

  handleResize() {
    this.setMediaHeight();
    if (this.mobileCarousel) {
      this.mobileCarousel.resize();
    }
    this.requestScrollUpdate();
  }

  handleSectionLoad(event) {
    if (!event.detail || !event.detail.sectionId) return;
    const section = this.closest('.shopify-section');
    if (!section || section.id !== `shopify-section-${event.detail.sectionId}`) return;
    this.setActive(0);
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  setMediaHeight() {
    if (!this.desktop || !this.panels.length || window.innerWidth < CtmRoomPreview.desktopBreakpoint) return;

    const sticky = this.querySelector('.ctm-roomprev_stky');
    const media = this.querySelector('.ctm-roomprev_media');
    const stickyHeight = sticky ? sticky.getBoundingClientRect().height : window.innerHeight;
    const mediaHeight = media ? media.getBoundingClientRect().height : window.innerHeight;
    const revealDistance = mediaHeight * Math.max(this.slideCount - 1, 0);
    const buffer = this.slideCount > 1 ? window.innerHeight * 0.06 : 0;
    const height = stickyHeight + revealDistance + buffer;

    if (height > 0) {
      this.style.setProperty('--ctm-roomprev-scroll', `${height.toFixed(1)}px`);
    }

    if (mediaHeight > 0) {
      this.style.setProperty('--ctm-roomprev-copy-h', `${mediaHeight.toFixed(1)}px`);
    }
  }

  setActive(index, options = {}) {
    if (this.slideCount <= 0) return;

    const nextIndex = this.clamp(Math.round(index), 0, this.slideCount - 1);
    const progress = this.clamp(index, 0, Math.max(this.slideCount - 1, 0));
    this.activeIndex = nextIndex;

    this.style.setProperty('--ctm-roomprev-active', nextIndex);
    this.style.setProperty('--ctm-roomprev-progress', progress);

    this.updatePanelsByProgress(nextIndex, progress);
    this.updateCopySlidesByProgress(progress);
    this.updateMobileSlides(nextIndex);
    this.updateDots(nextIndex);

    if (options.scrollDesktop) {
      this.scrollDesktopTo(nextIndex);
    }
  }

  resetPanelClips() {
    this.panels.forEach((panel, panelIndex) => {
      this.setPanelClip(panel, panelIndex === 0 ? 0 : 100);
      panel.dataset.active = panelIndex === 0 ? 'true' : 'false';
    });
  }

  updatePanelsByProgress(nextIndex, progress) {
    this.panels.forEach((panel, panelIndex) => {
      let clipProgress = panelIndex === 0 ? 0 : this.clamp((panelIndex - progress) * 100, 0, 100);

      if (this.reduceMotion.matches) {
        clipProgress = panelIndex <= nextIndex ? 0 : 100;
      }

      this.setPanelClip(panel, clipProgress);
      panel.dataset.active = panelIndex === nextIndex ? 'true' : 'false';
    });
  }

  updatePanelsLikeScrollingBanner(offsetTop) {
    let activeIndex = 0;

    if (offsetTop >= 0) {
      this.panels.forEach((panel, panelIndex) => {
        this.setPanelClip(panel, panelIndex === 0 ? 0 : 100);
        panel.dataset.active = panelIndex === 0 ? 'true' : 'false';
      });
      this.setActiveState(0, 0);
      return;
    }

    const scrolled = Math.abs(offsetTop);

    for (let panelIndex = 1; panelIndex < this.panels.length; panelIndex += 1) {
      const panel = this.panels[panelIndex];
      const media = this.querySelector('.ctm-roomprev_media');
      const panelHeight = media ? media.getBoundingClientRect().height : panel.getBoundingClientRect().height || window.innerHeight;
      const panelScrollTop = panelHeight * (panelIndex - 1);

      if (scrolled > panelScrollTop) {
        activeIndex = panelIndex;
        let clipProgress = 100 - Math.floor(((scrolled - panelScrollTop) / panelHeight) * 100);
        if (clipProgress > 95) clipProgress = 100;
        if (clipProgress < 5) clipProgress = 0;
        this.setPanelClip(panel, this.clamp(clipProgress, 0, 100));
      } else {
        this.setPanelClip(panel, 100);
      }
    }

    for (let panelIndex = activeIndex + 1; panelIndex < this.panels.length; panelIndex += 1) {
      this.setPanelClip(this.panels[panelIndex], 100);
    }

    const media = this.querySelector('.ctm-roomprev_media');
    const panelHeight = media ? media.getBoundingClientRect().height : window.innerHeight;
    const rawProgress = panelHeight > 0 ? this.clamp(scrolled / panelHeight, 0, this.slideCount - 1) : activeIndex;
    const syncedIndex = this.clamp(Math.round(rawProgress), 0, this.slideCount - 1);
    this.setActiveState(syncedIndex, rawProgress);
  }

  setPanelClip(panel, progress) {
    let clipPath;

    if (this.scrollDirection === -1) {
      const visible = 100 - progress;
      clipPath = `polygon(0 0, 100% 0, 100% ${visible}%, 0 ${visible}%)`;
    } else {
      clipPath = `polygon(0 ${progress}%, 100% ${progress}%, 100% 100%, 0 100%)`;
    }

    panel.style.setProperty('--ctm-roomprev-clip', clipPath);
    panel.style.clipPath = clipPath;
    panel.style.webkitClipPath = clipPath;
  }

  setActiveState(nextIndex, progressValue = nextIndex) {
    const progress = this.clamp(progressValue, 0, Math.max(this.slideCount - 1, 0));
    this.activeIndex = nextIndex;
    this.style.setProperty('--ctm-roomprev-active', nextIndex);
    this.style.setProperty('--ctm-roomprev-progress', progress);
    this.updateCopySlidesByProgress(progress);
    this.updateMobileSlides(nextIndex);
    this.updateDots(nextIndex);
  }

  updateCopySlidesByProgress(progress) {
    const readableIndex = this.clamp(Math.round(progress), 0, this.slideCount - 1);
    const direction = this.scrollDirection === -1 ? -1 : 1;
    const distance = this.getCopyDistance();

    this.copySlides.forEach((slide, slideIndex) => {
      let translate = (slideIndex - progress) * distance * direction;
      let isVisible = Math.abs(slideIndex - progress) < 1.02;
      let isReadable = slideIndex === readableIndex;

      if (this.reduceMotion.matches) {
        const activeIndex = this.clamp(Math.round(progress), 0, this.slideCount - 1);
        translate = slideIndex === activeIndex ? 0 : distance;
        isVisible = slideIndex === activeIndex;
        isReadable = slideIndex === activeIndex;
      }

      slide.style.opacity = isVisible ? '1' : '0';
      slide.style.transform = `translate3d(0, ${translate}px, 0)`;
      slide.dataset.active = isReadable ? 'true' : 'false';

      if (isReadable) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });
  }

  getCopyDistance() {
    const content = this.querySelector('[data-room-preview-content]');
    if (content) {
      const contentHeight = content.getBoundingClientRect().height;
      if (contentHeight > 0) return contentHeight;
    }

    return window.innerHeight;
  }

  updateMobileSlides(nextIndex) {
    this.mobileSlides.forEach((slide, slideIndex) => {
      const slideDots = Array.from(slide.querySelectorAll('[data-room-preview-dot]'));

      if (slideIndex === nextIndex) {
        slide.removeAttribute('aria-hidden');
        slideDots.forEach((dot) => dot.removeAttribute('tabindex'));
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slideDots.forEach((dot) => dot.setAttribute('tabindex', '-1'));
      }
    });
  }

  updateDots(nextIndex) {
    this.dots.filter((dot) => dot.closest('[data-room-preview-desktop]')).forEach((dot) => {
      dot.setAttribute('aria-current', Number(dot.dataset.index) === nextIndex ? 'true' : 'false');
    });
  }

  scrollDesktopTo(nextIndex) {
    if (!this.desktop || this.slideCount <= 1) return;

    const rect = this.desktop.getBoundingClientRect();
    const maxScroll = this.desktop.offsetHeight - window.innerHeight;
    const target = window.scrollY + rect.top + maxScroll * (nextIndex / (this.slideCount - 1));

    window.scrollTo({
      top: target,
      behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
    });
  }

  requestScrollUpdate() {
    if (this.ticking) return;

    window.requestAnimationFrame(() => {
      this.updateFromScroll();
      this.ticking = false;
    });

    this.ticking = true;
  }

  updateFromScroll() {
    if (!this.desktop || this.slideCount <= 1 || window.innerWidth < CtmRoomPreview.desktopBreakpoint) return;

    const scrollTop = window.scrollY;
    if (this.currentScrollTop === scrollTop) return;
    this.currentScrollTop = scrollTop;

    const rect = this.desktop.getBoundingClientRect();
    const maxScroll = this.desktop.offsetHeight - window.innerHeight;

    if (maxScroll <= 0) {
      this.setActive(0);
      return;
    }

    this.updatePanelsLikeScrollingBanner(rect.top);
  }
}

if (!customElements.get('ctm-room-preview')) {
  customElements.define('ctm-room-preview', CtmRoomPreview);
}

class CtmDetailPop extends HTMLElement {
  constructor() {
    super();
    this.sectionId = null;
    this.sliderElement = null;
    this.lastScrollLeft = 0;
    this.boundClick = this.handleClick.bind(this);
    this.boundModalAfterHide = this.handleModalAfterHide.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.sliderElement = this.querySelector('[data-detail-pop-slider]');
    this.addEventListener('click', this.boundClick);
    document.addEventListener('modal:afterHide', this.boundModalAfterHide);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundClick);
    document.removeEventListener('modal:afterHide', this.boundModalAfterHide);
  }

  handleClick(event) {
    const trigger = event.target.closest('[aria-controls^="CtmDetailPop-"]');
    if (!trigger) return;

    this.lastScrollLeft = this.sliderElement?.scrollLeft || 0;
  }

  handleModalAfterHide(event) {
    if (!event.target.classList.contains('ctm-detailpop_pop')) return;
    if (!event.target.id.startsWith(`CtmDetailPop-${this.sectionId}-`)) return;

    window.requestAnimationFrame(() => {
      document.body.style.touchAction = '';
      if (this.sliderElement) {
        this.sliderElement.scrollLeft = this.lastScrollLeft;
      }
    });
  }
}

if (!customElements.get('ctm-detail-pop')) {
  customElements.define('ctm-detail-pop', CtmDetailPop);
}

class CtmProductList extends HTMLElement {
  constructor() {
    super();
    this.grid = null;
    this.items = [];
    this.layoutButtons = [];
    this.defaultLayout = 'double';
    this.desktopLayout = 'double';
    this.boundResize = null;
    this.sliders = [];
    this.hoverBindings = [];
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-ctm-grid]');
    this.items = Array.from(this.querySelectorAll('[data-ctm-item]'));
    this.layoutButtons = Array.from(this.querySelectorAll('[data-ctm-layout]'));

    this.layoutButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.setLayout(btn.getAttribute('data-ctm-layout'), { syncUrl: true }));
    });

    this.setupResponsiveLayout();
    this.bindItemLinks();
    this.initMediaSliders();
    this.initListMotion();
    this.applyResponsiveLayout();
  }

  disconnectedCallback() {
    this.sliders.forEach((flkty) => {
      if (flkty && typeof flkty.destroy === 'function') flkty.destroy();
    });
    this.sliders = [];
    this.hoverBindings.forEach(({ media, onMove, onLeave }) => {
      media.removeEventListener('mousemove', onMove);
      media.removeEventListener('mouseleave', onLeave);
    });
    this.hoverBindings = [];
    this.teardownResponsiveLayout();
  }

  bindItemLinks() {
    this.items.forEach((item) => {
      const link = item.getAttribute('data-ctm-link');
      if (!link) return;

      const openLink = () => {
        if (item.dataset.ctmLinkExternal === 'true') {
          window.open(link, '_blank', 'noopener');
          return;
        }
        window.location.href = link;
      };

      item.addEventListener('click', (event) => {
        if (event.target.closest('.ctm-prdlist_dots, .ctm-prdlist_dot, .flickity-button, .ctm-prdlist_cta, button, a')) return;
        openLink();
      });

      item.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          openLink();
        }
      });
    });
  }

  setupResponsiveLayout() {
    this.defaultLayout = this.dataset.defaultLayout || 'double';
    this.desktopLayout = this.dataset.initialLayout || this.defaultLayout;
    this.grid?.setAttribute('data-layout', this.dataset.initialLayout || this.defaultLayout || 'double');
    this.boundResize = () => this.applyResponsiveLayout();
    window.addEventListener('resize', this.boundResize);
  }

  teardownResponsiveLayout() {
    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize);
    }
  }

  isMobileViewport() {
    return window.innerWidth < 768;
  }

  syncLayoutToUrl(layout) {
    const value = layout === 'single' ? '1' : '2';
    const nextUrl = new URL(window.location.href);
    if (nextUrl.searchParams.get('per_row') === value) return;
    nextUrl.searchParams.set('per_row', value);
    window.history.replaceState({}, '', nextUrl.toString());
  }

  applyResponsiveLayout() {
    const nextLayout = this.isMobileViewport() ? 'single' : this.desktopLayout;
    this.setLayout(nextLayout, { persist: false, syncUrl: false });
  }

  setLayout(mode, options = {}) {
    const { persist = true, syncUrl = false } = options;
    const next = mode === 'single' ? 'single' : 'double';
    this.grid?.setAttribute('data-layout', next);
    this.layoutButtons.forEach((btn) => {
      const active = btn.getAttribute('data-ctm-layout') === next;
      btn.setAttribute('aria-pressed', String(active));
    });
    if (persist) {
      this.desktopLayout = next;
    }
    if (syncUrl) {
      this.syncLayoutToUrl(next);
    }

    window.requestAnimationFrame(() => this.resizeSliders());
  }

  resizeSliders() {
    this.sliders.forEach((flkty) => {
      if (flkty && typeof flkty.resize === 'function') flkty.resize();
    });
  }

  bindDots(dotsWrap, total, onSelect) {
    dotsWrap.innerHTML = '';
    for (let index = 0; index < total; index += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ctm-prdlist_dot';
      dot.setAttribute('aria-label', `Go to image ${index + 2}`);
      dot.setAttribute('aria-current', 'false');
      dot.addEventListener('click', () => onSelect(index + 1));
      dotsWrap.appendChild(dot);
    }
  }

  setDotCurrent(dotsWrap, activeIndex) {
    dotsWrap.querySelectorAll('.ctm-prdlist_dot').forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === activeIndex - 1 ? 'true' : 'false');
    });
  }

  initMediaSlider(item) {
    const track = item.querySelector('[data-ctm-track]');
    const media = item.querySelector('[data-ctm-media]');
    const dotsWrap = item.querySelector('[data-ctm-dots]');
    if (!track || !dotsWrap || !media) return;
    if (track.classList.contains('flickity-enabled')) return;

    const slides = Array.from(track.children);
    if (slides.length <= 1) {
      dotsWrap.remove();
      return;
    }

    const dotCount = slides.length > 2 ? slides.length - 1 : 0;
    if (dotCount === 0) {
      dotsWrap.remove();
    }

    const flkty = new Flickity(track, {
      accessibility: false,
      adaptiveHeight: false,
      cellAlign: 'left',
      contain: true,
      draggable: true,
      selectedAttraction: 0.015,
      friction: 0.32,
      pageDots: false,
      prevNextButtons: false,
      setGallerySize: false,
      rightToLeft: window.theme?.config?.rtl ?? false,
      wrapAround: true,
    });

    this.sliders.push(flkty);
    if (dotCount > 0) {
      this.bindDots(dotsWrap, dotCount, (index) => flkty.select(index));
      flkty.on('change', (index) => this.setDotCurrent(dotsWrap, index));
    }
    this.bindHoverSwitch(media, flkty, slides.length);
  }

  initMediaSliders() {
    if (typeof Flickity !== 'function') return;
    this.items.forEach((item) => this.initMediaSlider(item));
  }

  bindHoverSwitch(media, flkty, mediaCount) {
    if (!media || !flkty || mediaCount <= 1) return;
    if (this.isMobileViewport()) return;

    const getHoverIndex = (mouseX, width) => {
      const zones = mediaCount - 1;
      const zoneWidth = width / zones;
      return Math.max(1, Math.min(zones, Math.floor(mouseX / zoneWidth) + 1));
    };

    const onMove = (event) => {
      const rect = media.getBoundingClientRect();
      if (!rect.width) return;
      const mouseX = event.clientX - rect.left;
      const nextIndex = getHoverIndex(mouseX, rect.width);
      flkty.select(nextIndex);
    };

    const onLeave = () => {
      flkty.select(0);
    };

    media.addEventListener('mousemove', onMove);
    media.addEventListener('mouseleave', onLeave);
    this.hoverBindings.push({ media, onMove, onLeave });
  }

  getListMotionParams() {
    const isMobile = this.isMobileViewport();
    return {
      distance: isMobile ? 30 : 50,
      duration: isMobile ? 0.3 : 0.5,
      staggerDelay: isMobile ? 0.05 : 0.1,
    };
  }

  initListMotion() {
    if (!this.items.length) return;

    const { distance } = this.getListMotionParams();
    Motion.animate(this.items, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: 0 });
    Motion.inView(this, this.playListMotion.bind(this));
  }

  playListMotion() {
    const { distance, duration, staggerDelay } = this.getListMotionParams();
    Motion.animate(
      this.items,
      { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] },
      { duration, delay: Motion.stagger(staggerDelay) }
    ).finished.then(() => {
      this.items.forEach((item) => item.style.removeProperty('transform'));
    });
  }
}

if (!customElements.get('ctm-product-list')) {
  customElements.define('ctm-product-list', CtmProductList);
}
class CtmSellPoint extends HTMLElement {
  static mediaTransitionDuration = 860;

  constructor() {
    super();
    this.tabs = [];
    this.panels = [];
    this.desktopSummaries = [];
    this.mainMedia = [];
    this.overlayMedia = [];
    this.dots = [];
    this.prevButton = null;
    this.nextButton = null;
    this.slideshow = null;
    this.mobileCards = [];
    this.mobileCarousel = null;
    this.mobileDots = [];
    this.tablist = null;
    this.activeIndex = -1;
    this.defaultIndex = 0;
    this.hoverResetTimer = null;
    this.hoverActivateTimer = null;
    this.mediaTransitionTimers = new WeakMap();
    this.tabTransitionTimers = new WeakMap();
    this.summaryTransitionTimers = new WeakMap();
    this.scrollRaf = null;
    this.tabIndicatorRaf = null;
    this.tabIndicatorUntil = 0;
    this.mobileScrollActive = false;
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.prepareTransitionElement = this.prepareTransitionElement.bind(this);
  }

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('.ctm-sellpoint_trg[role="tab"]'));
    this.tablist = this.querySelector('.ctm-sellpoint_tabs');
    this.isSingleImageMode = this.classList.contains('ctm-sellpoint--single-img');
    this.singleImageTrigger = this.dataset.singleImageTrigger || 'click';
    this.panels = Array.from(this.querySelectorAll('.ctm-sellpoint_pnl[role="tabpanel"]'));
    this.desktopSummaries = Array.from(this.querySelectorAll('[data-summary-index]'));
    this.mainMedia = Array.from(this.querySelectorAll('[data-media-type="main"]'));
    this.overlayMedia = Array.from(this.querySelectorAll('[data-media-type="overlay"]'));
    this.singleMedia = Array.from(this.querySelectorAll('[data-media-type="single"]'));
    this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.slideshow = this.querySelector('[data-mobile-track]');
    this.mobileCards = Array.from(this.querySelectorAll('[data-mobile-card]'));
    this.mobileDots = Array.from(this.querySelectorAll('[data-mobile-dot]'));
    this.mediaQuery = window.matchMedia('(max-width: 74.9rem)');

    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', (event) => {
        if (this.shouldUseHoverActivation() && event.detail > 0) return;
        this.activate(index, { moveFocus: false, userInitiated: true });
      });
      tab.addEventListener('focus', () => {
        if (this.shouldUseHoverActivation()) {
          this.activate(index, { moveFocus: false, scrollIntoView: false });
        }
      });
      tab.addEventListener('mouseenter', () => {
        if (this.shouldUseHoverActivation()) {
          this.clearHoverReset();
          this.scheduleHoverActivate(index);
        }
      });
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.activate(index, { moveFocus: false, userInitiated: true }));
    });

    this.mobileDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.syncMobileScroller(index, { instant: false }));
    });

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goToPrevious({ userInitiated: true }));
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goToNext({ userInitiated: true }));
    }

    this.addEventListener('mouseleave', () => {
      if (this.shouldUseHoverActivation()) {
        this.scheduleHoverReset();
      }
    });
    this.addEventListener('mouseenter', () => this.clearHoverReset());

    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    window.addEventListener('resize', this.handleResize);
    this.mediaQuery.addEventListener('change', this.handleViewportChange);

    this.prepareTransitionElements();

    this.defaultIndex = this.getInitialIndex();
    this.activate(this.defaultIndex, { moveFocus: false, scrollIntoView: false });
    this.updateTabIndicator(this.defaultIndex, true);
    this.initMobileScroller();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.removeEventListener('resize', this.handleResize);
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleViewportChange);
    }
    this.destroyMobileScroller();
    this.clearHoverReset();
    this.clearHoverActivate();
    this.stopTabIndicatorTracking();
  }

  shouldUseHoverActivation() {
    if (!this.canHoverDesktop()) return false;
    return !this.isSingleImageMode || this.singleImageTrigger === 'hover';
  }

  onKeydown(event, index) {
    let nextIndex = index;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (index + 1) % this.tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.activate(index, { moveFocus: false, userInitiated: true });
        return;
      default:
        return;
    }

    event.preventDefault();
    this.activate(nextIndex, { moveFocus: true, userInitiated: true });
  }

  activate(index, { moveFocus = false, scrollIntoView = true, userInitiated = false } = {}) {
    if (index < 0 || index >= this.tabs.length) return;
    if (index === this.activeIndex) return;

    const shouldAnimate = this.activeIndex !== -1 && this.activeIndex !== index;

    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      const wasActive = tab.classList.contains('is-active');
      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${panelId}`) : this.panels[tabIndex];

      this.updateTabTransitionState(tab, isActive, wasActive);

      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', '0');
      tab.classList.toggle('is-active', isActive);

      if (panel) {
        panel.toggleAttribute('hidden', !isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        panel.classList.toggle('is-active', isActive);
        if (isActive && shouldAnimate) {
          this.animateElement(panel, 'is-panel-entering');
        }
      }
    });

    this.toggleSummaries(index, shouldAnimate);
    this.toggleMedia(this.mainMedia, index, shouldAnimate);
    this.toggleMedia(this.overlayMedia, index, shouldAnimate);
    this.toggleMedia(this.singleMedia, index, shouldAnimate);
    this.toggleDots(index);
    this.toggleMobileCards(index);
    this.activeIndex = index;
    this.updateTabIndicator(index);

    if (scrollIntoView) {
      if (this.isMobileViewport()) {
        this.syncMobileScroller(index, { instant: !userInitiated });
      } else if (this.tabs[index]) {
        this.tabs[index].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    if (moveFocus && this.tabs[index]) {
      this.tabs[index].focus();
    }
  }

  goToNext(options = {}) {
    const nextIndex = (this.activeIndex + 1) % this.tabs.length;
    this.activate(nextIndex, options);
  }

  goToPrevious(options = {}) {
    const previousIndex = (this.activeIndex - 1 + this.tabs.length) % this.tabs.length;
    this.activate(previousIndex, options);
  }

  toggleMedia(mediaItems, activeIndex) {
    mediaItems.forEach((mediaItem, index) => {
      const isActive = index === activeIndex;
      const wasActive = mediaItem.classList.contains('is-active');
      const wasLeaving = mediaItem.classList.contains('is-leaving');

      mediaItem.removeAttribute('hidden');
      mediaItem.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      const transitionTimer = this.mediaTransitionTimers.get(mediaItem);
      if (transitionTimer) {
        if (typeof transitionTimer === 'number') {
          window.clearTimeout(transitionTimer);
        } else {
          window.clearTimeout(transitionTimer.timer);
          window.cancelAnimationFrame(transitionTimer.frame);
        }
        this.mediaTransitionTimers.delete(mediaItem);
      }

      mediaItem.classList.remove('is-entering', 'is-leaving');

      if (isActive) {
        if (wasActive && !wasLeaving) {
          mediaItem.classList.add('is-active');
          return;
        }

        mediaItem.classList.add('is-entering');
        mediaItem.style.opacity = '0';
        void mediaItem.offsetWidth;

        const frame = window.requestAnimationFrame(() => {
          if (mediaItem.getAttribute('aria-hidden') === 'true') return;
          mediaItem.style.opacity = '';
          mediaItem.classList.add('is-active');
        });

        const enteringTimer = window.setTimeout(() => {
          mediaItem.classList.remove('is-entering');
          this.mediaTransitionTimers.delete(mediaItem);
        }, CtmSellPoint.mediaTransitionDuration);
        this.mediaTransitionTimers.set(mediaItem, { frame, timer: enteringTimer });
        return;
      }

      mediaItem.classList.remove('is-active');

      if (wasActive) {
        mediaItem.classList.add('is-leaving');
        const leavingTimer = window.setTimeout(() => {
          mediaItem.classList.remove('is-leaving');
          this.mediaTransitionTimers.delete(mediaItem);
        }, CtmSellPoint.mediaTransitionDuration);
        this.mediaTransitionTimers.set(mediaItem, leavingTimer);
      }
    });
  }

  toggleSummaries(activeIndex, shouldAnimate = false) {
    this.desktopSummaries.forEach((summary, index) => {
      const isActive = index === activeIndex;
      const wasActive = summary.classList.contains('is-active');

      summary.removeAttribute('hidden');
      summary.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      summary.classList.toggle('is-active', isActive);
      this.updateSummaryTransitionState(summary, isActive, wasActive);
    });
  }

  updateTabIndicator(activeIndex, instant = false) {
    if (!this.tablist) return;

    const activeTab = this.tabs[activeIndex];
    if (!activeTab) return;

    const tablistRect = this.tablist.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const top = tabRect.top - tablistRect.top;

    this.tablist.classList.toggle('is-indicator-instant', instant);
    this.tablist.style.setProperty('--ctm-sellpoint-ind-top', `${Math.max(top, 0).toFixed(2)}px`);
    this.tablist.style.setProperty('--ctm-sellpoint-ind-height', `${Math.max(tabRect.height, 1).toFixed(2)}px`);
    this.trackTabIndicator(activeIndex);

    if (instant) {
      window.requestAnimationFrame(() => {
        this.tablist?.classList.remove('is-indicator-instant');
      });
    }
  }

  trackTabIndicator(activeIndex) {
    this.tabIndicatorUntil = window.performance.now() + 760;
    if (this.tabIndicatorRaf) return;

    const update = () => {
      this.tabIndicatorRaf = null;
      if (window.performance.now() >= this.tabIndicatorUntil) return;

      const activeTab = this.tabs[activeIndex];
      if (!this.tablist || !activeTab) return;

      const tablistRect = this.tablist.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      const top = tabRect.top - tablistRect.top;

      this.tablist.style.setProperty('--ctm-sellpoint-ind-top', `${Math.max(top, 0).toFixed(2)}px`);
      this.tablist.style.setProperty('--ctm-sellpoint-ind-height', `${Math.max(tabRect.height, 1).toFixed(2)}px`);
      this.tabIndicatorRaf = window.requestAnimationFrame(update);
    };

    this.tabIndicatorRaf = window.requestAnimationFrame(update);
  }

  stopTabIndicatorTracking() {
    if (!this.tabIndicatorRaf) return;

    window.cancelAnimationFrame(this.tabIndicatorRaf);
    this.tabIndicatorRaf = null;
  }

  updateTabTransitionState(tab, isActive, wasActive) {
    this.clearTimedClass(tab, this.tabTransitionTimers);
    tab.classList.remove('is-tab-entering', 'is-tab-leaving');

    if (isActive && !wasActive) {
      tab.classList.add('is-tab-entering');
      this.setTimedClass(tab, 'is-tab-entering', this.tabTransitionTimers, 640);
      return;
    }

    if (!isActive && wasActive) {
      tab.classList.add('is-tab-leaving');
      this.setTimedClass(tab, 'is-tab-leaving', this.tabTransitionTimers, 520);
    }
  }

  updateSummaryTransitionState(summary, isActive, wasActive) {
    this.clearTimedClass(summary, this.summaryTransitionTimers);
    summary.classList.remove('is-summary-entering', 'is-summary-leaving');

    if (isActive && !wasActive) {
      summary.classList.add('is-summary-entering');
      this.setTimedClass(summary, 'is-summary-entering', this.summaryTransitionTimers, 720);
      return;
    }

    if (!isActive && wasActive) {
      summary.classList.add('is-summary-leaving');
      this.setTimedClass(summary, 'is-summary-leaving', this.summaryTransitionTimers, 520);
    }
  }

  setTimedClass(element, className, timerMap, duration) {
    const timer = window.setTimeout(() => {
      element.classList.remove(className);
      timerMap.delete(element);
    }, duration);
    timerMap.set(element, timer);
  }

  clearTimedClass(element, timerMap) {
    const timer = timerMap.get(element);
    if (!timer) return;

    window.clearTimeout(timer);
    timerMap.delete(element);
  }

  prepareTransitionElements() {
    [
      ...this.desktopSummaries,
      ...this.mainMedia,
      ...this.overlayMedia,
      ...this.singleMedia,
    ].forEach(this.prepareTransitionElement);
  }

  prepareTransitionElement(element) {
    element.removeAttribute('hidden');
    element.setAttribute('aria-hidden', element.classList.contains('is-active') ? 'false' : 'true');
  }

  toggleDots(activeIndex) {
    this.dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  toggleMobileCards(activeIndex) {
    this.mobileCards.forEach((card, index) => {
      const isActive = index === activeIndex;
      card.classList.toggle('is-active', isActive);
      if (isActive) {
        card.removeAttribute('aria-hidden');
      } else {
        card.setAttribute('aria-hidden', 'true');
      }
    });
  }

  toggleMobileDots(activeIndex) {
    this.mobileDots.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }

  initMobileScroller() {
    if (!this.slideshow || !this.mobileCards.length || !this.isMobileViewport()) return;
    if (this.mobileScrollActive) return;

    if (!this.mobileCarousel && this.mobileCards.length > 1 && typeof Flickity !== 'undefined') {
      this.mobileCarousel = new Flickity(this.slideshow, {
        accessibility: false,
        adaptiveHeight: true,
        cellAlign: 'left',
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        rightToLeft: theme.config.rtl,
        wrapAround: false,
      });

      this.mobileCarousel.on('change', (index) => {
        this.setMobileActive(index);
        this.dispatchEvent(new CustomEvent('slider:change', {
          detail: {
            currentPage: index + 1,
            currentElement: this.mobileCards[index],
          },
        }));
      });
    }

    this.mobileScrollActive = true;
    window.requestAnimationFrame(() => {
      this.syncMobileScroller(this.activeIndex > -1 ? this.activeIndex : 0, { instant: true });
      this.mobileCarousel?.resize();
    });
  }

  destroyMobileScroller() {
    if (!this.slideshow) return;

    this.mobileScrollActive = false;
    if (this.mobileCarousel) {
      this.mobileCarousel.destroy();
      this.mobileCarousel = null;
    }
    if (this.scrollRaf) {
      window.cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = null;
    }
  }

  getInitialIndex() {
    const editorIndex = this.getEditorSelectedIndex();
    if (editorIndex > -1) return editorIndex;

    const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    return selectedIndex > -1 ? selectedIndex : 0;
  }

  getEditorSelectedIndex(blockId = null) {
    const selectedBlockId = blockId || new URLSearchParams(window.location.search).get('block');
    if (!selectedBlockId) return -1;

    const matchingTab = this.tabs.find((tab) => {
      const editorBlock = tab.getAttribute('data-shopify-editor-block');
      return editorBlock ? editorBlock.indexOf(selectedBlockId) > -1 : false;
    });

    return matchingTab ? this.tabs.indexOf(matchingTab) : -1;
  }

  setMobileActive(index) {
    if (typeof index !== 'number' || index < 0 || index >= this.mobileCards.length) return;

    this.activeIndex = index;
    this.toggleMobileCards(index);
    this.toggleMobileDots(index);
  }

  syncMobileScroller(index, { instant = false } = {}) {
    if (!this.slideshow || !this.mobileCards.length || index < 0 || index >= this.mobileCards.length) {
      return;
    }

    if (this.mobileCarousel) {
      this.mobileCarousel.select(index, false, instant);
      return;
    }

    const card = this.mobileCards[index];
    const left = card.offsetLeft - this.slideshow.offsetLeft;
    this.slideshow.scrollTo({
      left,
      behavior: instant ? 'auto' : 'smooth',
    });
  }

  isMobileViewport() {
    return this.mediaQuery ? this.mediaQuery.matches : window.matchMedia('(max-width: 74.9rem)').matches;
  }

  handleViewportChange() {
    if (this.isMobileViewport()) {
      this.initMobileScroller();
      this.syncMobileScroller(this.activeIndex > -1 ? this.activeIndex : this.getInitialIndex(), { instant: true });
    } else {
      this.destroyMobileScroller();
      this.updateTabIndicator(this.activeIndex > -1 ? this.activeIndex : this.getInitialIndex(), true);
    }
  }

  handleResize() {
    this.updateTabIndicator(this.activeIndex > -1 ? this.activeIndex : this.getInitialIndex(), true);
  }

  animateElement(element, className) {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
  }

  handleBlockSelect(event) {
    const blockId = event.detail && event.detail.blockId;
    if (!blockId) return;

    const index = this.getEditorSelectedIndex(blockId);
    if (index < 0) return;

    this.activate(index, { moveFocus: !this.isMobileViewport(), userInitiated: true });
  }

  canHoverDesktop() {
    return window.matchMedia('(min-width: 75rem) and (hover: hover) and (pointer: fine)').matches;
  }

  scheduleHoverReset() {
    this.clearHoverReset();
    this.clearHoverActivate();
    this.hoverResetTimer = window.setTimeout(() => {
      this.activate(this.defaultIndex, { moveFocus: false, scrollIntoView: false });
    }, 180);
  }

  clearHoverReset() {
    if (!this.hoverResetTimer) return;

    window.clearTimeout(this.hoverResetTimer);
    this.hoverResetTimer = null;
  }

  scheduleHoverActivate(index) {
    this.clearHoverActivate();
    this.hoverActivateTimer = window.setTimeout(() => {
      this.activate(index, { moveFocus: false, scrollIntoView: false });
    }, 70);
  }

  clearHoverActivate() {
    if (!this.hoverActivateTimer) return;

    window.clearTimeout(this.hoverActivateTimer);
    this.hoverActivateTimer = null;
  }
}

if (!customElements.get('ctm-sell-point')) {
  customElements.define('ctm-sell-point', CtmSellPoint);
}



/* ctm img lazy start */
class CtmLazyImg extends HTMLElement {
  connectedCallback() {
    if (this._ctmLazyImgInitialized) return;
    this._ctmLazyImgInitialized = true;
    this.rootLazyMargin = this.dataset.rootLazyMargin || '0px 0px 150% 0px';
    this.rootMargin = this.dataset.rootMargin || '0px 0px 75% 0px';
    this.lazyPerformance();
    this.imgPerformance();
  }

  intersectionObserverItem({ rootMargin, fn }) {
    new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof fn === 'function') fn();
          observer.unobserve(this);
        }
      });
    }, { rootMargin }).observe(this);
  }

  lazyPerformance() {
    this.intersectionObserverItem({
      rootMargin: this.rootLazyMargin,
      fn: () => {
        const img = this.querySelector('img.hide');
        if (!img) return;
        img.classList.remove('hide');
      }
    });
  }

  imgPerformance() {
    this.intersectionObserverItem({
      rootMargin: this.rootMargin,
      fn: () => {
        const img = this.querySelector('img');
        if (!img || !img.dataset.srcset || img.getAttribute('srcset') === img.dataset.srcset) return;
        img.srcset = img.dataset.srcset;
      }
    });
  }
}

if (!customElements.get('ctm-img-lazy')) {
  customElements.define('ctm-img-lazy', CtmLazyImg);
}
/* ctm img lazy end */

/* CTM FAQ components start */
class CtmFaqA11y {
  constructor(host) {
    this.host = host;
  }

  closeOpenDetails(event) {
    if (event.key !== 'Escape') return false;

    const item = event.target.closest('.ctm-faqtab__item[open]');
    if (!item) return false;

    event.preventDefault();
    item.open = false;
    item.querySelector('summary')?.focus();
    return true;
  }

  getNextTab(event, tabs, currentTab) {
    const currentIndex = tabs.indexOf(currentTab);
    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return null;
    }

    return tabs[nextIndex];
  }

  setActiveTab(tabs, panels, activeTab) {
    const activeKey = activeTab.dataset.tabKey;

    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.tabKey !== activeKey;
    });

  }

  collapseDisclosureOnEscape(event, group) {
    if (event.key !== 'Escape' || !group.classList.contains('is-open')) return false;

    event.preventDefault();
    group.classList.remove('is-open');
    return true;
  }

  syncDisclosure(toggle, panel, { expanded, interactive }) {
    toggle.tabIndex = interactive ? 0 : -1;
    toggle.setAttribute('aria-expanded', String(expanded));
    panel.hidden = !expanded;
  }

  setCurrentLink(links, activeLink) {
    links.forEach((link) => {
      if (link === activeLink) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }

  configureSearchOption(option, index, id) {
    option.id = id;
    option.dataset.index = index;
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');
  }

  setSearchExpanded(expanded) {
    const { input, results } = this.host;
    results.hidden = !expanded;
    results.dataset.open = String(expanded);
    input.setAttribute('aria-expanded', String(expanded));

    if (!expanded) input.removeAttribute('aria-activedescendant');
  }

  setSearchActive(index) {
    const { input, resultsList } = this.host;
    this.host.activeIndex = index;

    resultsList.querySelectorAll('[role="option"]').forEach((option, optionIndex) => {
      const isActive = optionIndex === index;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-selected', String(isActive));
      if (isActive) input.setAttribute('aria-activedescendant', option.id);
    });
  }

  announceSearch(message) {
    this.host.status.textContent = message;
  }

  handleSearchKeydown(event) {
    const { activeIndex, results, visibleEntries } = this.host;

    if (event.key === 'Escape') {
      if (!results.hidden) event.preventDefault();
      this.setSearchExpanded(false);
      return;
    }

    if (results.hidden || !visibleEntries.length) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = (activeIndex + 1) % visibleEntries.length;
        break;
      case 'ArrowUp':
        nextIndex = (activeIndex - 1 + visibleEntries.length) % visibleEntries.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = visibleEntries.length - 1;
        break;
      case 'Enter':
        event.preventDefault();
        this.host.activateEntry(visibleEntries[Math.max(0, activeIndex)]);
        return;
      default:
        return;
    }

    event.preventDefault();
    this.setSearchActive(nextIndex);
  }

  focus(element, options) {
    element.focus(options);
  }
}

class CtmFaqElement extends HTMLElement {
  constructor() {
    super();
    this.a11y = new CtmFaqA11y(this);
  }

  getHeaderOffset() {
    const headerSection = document.querySelector('.header-section');
    const rootStyles = getComputedStyle(document.documentElement);
    const stickyHeight = Number.parseFloat(rootStyles.getPropertyValue('--sticky-header-height')) || 0;
    const headerHeight = Number.parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
    const header = headerSection?.querySelector('header[is="sticky-header"], header') ||
      document.querySelector('header[is="sticky-header"], header');
    const fallbackHeaderHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
    const visibleHeaderHeight = Math.max(stickyHeight, headerHeight, fallbackHeaderHeight);
    const isHeaderHidden = headerSection?.classList.contains('header-hidden') &&
      headerSection.getAttribute('data-sticky-type') === 'on-scroll-up';
    const stickyGap = 10;

    return (isHeaderHidden ? 0 : visibleHeaderHeight) + stickyGap;
  }
}

class CtmFaqTab extends CtmFaqElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.tabs = Array.from(this.querySelectorAll('[data-ctm-faqtab-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-ctm-faqtab-panel]'));

    this.addEventListener('click', this, { signal: this.abortController.signal });
    this.addEventListener('keydown', this, { signal: this.abortController.signal });

    const initialTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.activate(initialTab);
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  handleEvent(event) {
    const tab = event.target.closest('[data-ctm-faqtab-trigger]');

    if (event.type === 'click' && tab && this.contains(tab)) {
      this.activate(tab, { scrollIntoView: true });
      return;
    }

    if (event.type !== 'keydown') return;

    if (this.a11y.closeOpenDetails(event)) return;

    if (!tab || !this.contains(tab)) return;

    const nextTab = this.a11y.getNextTab(event, this.tabs, tab);
    if (!nextTab) return;

    event.preventDefault();
    this.activate(nextTab, {
      moveFocus: true,
      scrollIntoView: true
    });
  }

  activate(activeTab, { moveFocus = false, scrollIntoView = false } = {}) {
    if (!activeTab) return;

    this.a11y.setActiveTab(this.tabs, this.panels, activeTab);

    if (scrollIntoView) {
      activeTab.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }

    if (moveFocus) this.a11y.focus(activeTab);
  }
}
customElements.define('ctm-faq-tab', CtmFaqTab);

class CtmFaqGrid extends CtmFaqElement {
  static mobileQuery = '(max-width: 749px)';

  connectedCallback() {
    this.abortController = new AbortController();
    this.mobileQuery = window.matchMedia(CtmFaqGrid.mobileQuery);
    this.headerSection = document.querySelector('.header-section');
    this.groups = Array.from(this.querySelectorAll('[data-ctm-faqgrid-group]'));
    this.links = Array.from(this.querySelectorAll('.ctm-faqgrid__nav-item[href^="#"]'));
    this.searchBox = this.getSearchBox();
    this.stickyTopFallback = Number.parseFloat(
      getComputedStyle(this).getPropertyValue('--ctm-faqgrid-sticky-top')
    ) || 0;

    this.addEventListener('click', this, { signal: this.abortController.signal });
    this.addEventListener('keydown', this, { signal: this.abortController.signal });
    this.mobileQuery.addEventListener('change', this, { signal: this.abortController.signal });
    window.addEventListener('scroll', this, { passive: true, signal: this.abortController.signal });
    window.addEventListener('resize', this, { signal: this.abortController.signal });

    if (this.headerSection) {
      this.headerObserver = new MutationObserver(() => this.requestStickyUpdate());
      this.headerObserver.observe(this.headerSection, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    if (this.searchBox) {
      this.searchResizeObserver = new ResizeObserver(() => this.requestStickyUpdate());
      this.searchResizeObserver.observe(this.searchBox);
    }

    this.applyAccordionMode();
    this.initNavObserver();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.headerObserver?.disconnect();
    this.searchResizeObserver?.disconnect();
    this.navObserver?.disconnect();

    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
  }

  handleEvent(event) {
    if (event.type === 'change') {
      this.applyAccordionMode();
      return;
    }

    if (event.type === 'scroll' || event.type === 'resize') {
      this.requestStickyUpdate();
      return;
    }

    const toggle = event.target.closest('[data-ctm-faqgrid-group-toggle]');
    if (!toggle || !this.contains(toggle) || !this.mobileQuery.matches) return;

    const group = toggle.closest('[data-ctm-faqgrid-group]');

    if (event.type === 'keydown') {
      if (!this.a11y.collapseDisclosureOnEscape(event, group)) return;
    } else {
      group.classList.toggle('is-open');
    }

    this.syncAccordionState();
  }

  getSearchBox() {
    const searchSections = Array.from(document.querySelectorAll('[data-ctm-faq-search-section]'));
    const precedingSearches = searchSections.filter(
      (candidate) => candidate.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_FOLLOWING
    );
    const searchSection = precedingSearches.pop() || searchSections[0];

    return searchSection?.querySelector('.ctm-faqsrch_box');
  }

  applyAccordionMode() {
    this.groups.forEach((group) => {
      group.classList.toggle('is-open', !this.mobileQuery.matches);
    });
    this.syncAccordionState();
    this.syncStickyTop();
  }

  syncAccordionState() {
    const isMobile = this.mobileQuery.matches;

    this.groups.forEach((group) => {
      const isOpen = group.classList.contains('is-open');
      const toggle = group.querySelector('[data-ctm-faqgrid-group-toggle]');
      const panel = group.querySelector('[data-ctm-faqgrid-group-panel]');

      this.a11y.syncDisclosure(toggle, panel, {
        expanded: !isMobile || isOpen,
        interactive: isMobile
      });
    });
  }

  syncStickyTop() {
    const nextTop = this.searchBox
      ? this.getHeaderOffset() + Math.ceil(this.searchBox.getBoundingClientRect().height) + 20
      : this.stickyTopFallback;

    this.style.setProperty('--ctm-faqgrid-sticky-top', `${nextTop}px`);
  }

  requestStickyUpdate() {
    if (this.stickyFrame) return;

    this.stickyFrame = requestAnimationFrame(() => {
      this.syncStickyTop();
      this.stickyFrame = null;
    });
  }

  initNavObserver() {
    const targets = this.links
      .map((link) => {
        const target = this.querySelector(`#${CSS.escape(link.hash.slice(1))}`);
        return target ? { link, target } : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    const setActive = (targetId) => {
      const activeTarget = targets.find(({ target }) => target.id === targetId);
      this.a11y.setCurrentLink(this.links, activeTarget?.link);
    };

    setActive(targets[0].target.id);
    this.navObserver = new IntersectionObserver(
      (entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visibleEntry) setActive(visibleEntry.target.id);
      },
      {
        rootMargin: '-20% 0px -65% 0px',
        threshold: [0.01, 0.1, 0.25, 0.5]
      }
    );

    targets.forEach(({ target }) => this.navObserver.observe(target));
  }
}
customElements.define('ctm-faq-grid', CtmFaqGrid);

class CtmFaqSearch extends CtmFaqElement {
  static gridSelector = 'ctm-faq-grid';
  static tabSelector = 'ctm-faq-tab';

  normalizeText(value) {
    return String(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^0-9a-z\u4e00-\u9fff]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.input = this.querySelector('.ctm-faqsrch_input');
    this.form = this.querySelector('.ctm-faqsrch_panel');
    this.searchBox = this.querySelector('.ctm-faqsrch_box');
    this.results = this.querySelector('[data-ctm-faqsrch-results]');
    this.resultsList = this.querySelector('[data-ctm-faqsrch-list]');
    this.status = this.querySelector('.ctm-faqsrch_status');
    this.spacer = this.querySelector('.ctm-faqsrch_spacer');
    this.headerSection = document.querySelector('.header-section');
    this.entries = this.buildIndex();
    this.visibleEntries = [];
    this.activeIndex = -1;
    this.lastQuery = '';
    this.highlightMarks = [];

    this.classList.add('ctm-faqsrch--local');
    this.input.addEventListener('input', this, { signal: this.abortController.signal });
    this.input.addEventListener('focus', this, { signal: this.abortController.signal });
    this.input.addEventListener('blur', this, { signal: this.abortController.signal });
    this.input.addEventListener('keydown', this, { signal: this.abortController.signal });
    this.form.addEventListener('submit', this, { signal: this.abortController.signal });
    this.resultsList.addEventListener('mousedown', this, { signal: this.abortController.signal });
    this.resultsList.addEventListener('mousemove', this, { signal: this.abortController.signal });
    this.resultsList.addEventListener('click', this, { signal: this.abortController.signal });
    document.addEventListener('click', this, { signal: this.abortController.signal });
    window.addEventListener('scroll', this, { passive: true, signal: this.abortController.signal });
    window.addEventListener('resize', this, { signal: this.abortController.signal });

    if (this.headerSection) {
      this.headerObserver = new MutationObserver(() => this.requestDockUpdate());
      this.headerObserver.observe(this.headerSection, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    this.syncDockTop();
    this.requestDockUpdate();
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.headerObserver?.disconnect();
    this.clearTargetHighlights();
    this.classList.remove('ctm-faqsrch--docked');
    this.spacer.hidden = true;
    this.spacer.style.height = '0px';
    this.dockAnchorTop = null;

    if (this.dockFrame) cancelAnimationFrame(this.dockFrame);
  }

  handleEvent(event) {
    if (event.type === 'scroll' || event.type === 'resize') {
      if (event.type === 'resize') this.dockAnchorTop = null;
      this.requestDockUpdate();
      return;
    }

    if (event.type === 'submit') {
      event.preventDefault();
      this.performSearch();
      return;
    }

    if (event.currentTarget === document) {
      if (!this.contains(event.target)) this.a11y.setSearchExpanded(false);
      return;
    }

    if (event.currentTarget === this.resultsList) {
      const option = event.target.closest('[role="option"]');
      if (!option) return;

      if (event.type === 'mousedown') {
        event.preventDefault();
      } else if (event.type === 'mousemove') {
        this.a11y.setSearchActive(Number(option.dataset.index));
      } else {
        this.activateEntry(this.visibleEntries[Number(option.dataset.index)]);
      }
      return;
    }

    if (event.type === 'input') {
      this.performSearch();
      this.syncDockSpacer();
    } else if (event.type === 'focus') {
      this.performSearch();
    } else if (event.type === 'blur') {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.a11y.setSearchExpanded(false);
      }, 120);
    } else if (event.type === 'keydown') {
      this.a11y.handleSearchKeydown(event);
    }
  }

  buildIndex() {
    const entries = [];
    const scopeRoots = this.getScopeRoots();

    scopeRoots.forEach((scopeRoot) => {
      const sourceLabel =
        scopeRoot.getAttribute('aria-label') ||
        scopeRoot.querySelector('.ctm-faqgrid__title, .ctm-faqtab__label')?.textContent.trim() ||
        '';

      if (scopeRoot.matches(CtmFaqSearch.gridSelector) || scopeRoot.querySelector(CtmFaqSearch.gridSelector)) {
        this.pushGridEntries(scopeRoot, sourceLabel, entries);
      }

      if (scopeRoot.matches(CtmFaqSearch.tabSelector) || scopeRoot.querySelector(CtmFaqSearch.tabSelector)) {
        this.pushTabEntries(scopeRoot, sourceLabel, entries);
      }
    });

    return entries;
  }

  getScopeRoots() {
    const scopeIds = this.dataset.searchScopeIds
      .split(/[,\s]+/)
      .filter(Boolean);

    if (scopeIds.length) {
      return scopeIds
        .map((id) => {
          const cleanId = id.replace(/^#/, '');
          return document.getElementById(cleanId) || document.getElementById(`shopify-section-${cleanId}`);
        })
        .filter(Boolean);
    }

    return Array.from(
      document.querySelectorAll(`${CtmFaqSearch.gridSelector}, ${CtmFaqSearch.tabSelector}`)
    );
  }

  pushGridEntries(scopeRoot, sourceLabel, entries) {
    scopeRoot.querySelectorAll('.ctm-faqgrid__group').forEach((group) => {
      const groupLabel = group.querySelector('.ctm-faqgrid__group-title')?.textContent.trim() || sourceLabel;
      const groupToggle = group.querySelector('[data-ctm-faqgrid-group-toggle]');

      group.querySelectorAll('.ctm-faqgrid__card').forEach((card) => {
        const title = card.querySelector('.ctm-faqgrid__card-title')?.textContent.trim();
        if (!title) return;

        const linkText = card.querySelector('.ctm-faqgrid__card-link')?.textContent.trim() || '';
        entries.push({
          kind: 'grid-card',
          title,
          meta: groupLabel,
          element: card,
          group,
          groupToggle,
          searchable: this.normalizeText([sourceLabel, groupLabel, title, linkText].join(' ')),
          order: entries.length
        });
      });
    });
  }

  pushTabEntries(scopeRoot, sourceLabel, entries) {
    scopeRoot.querySelectorAll('.ctm-faqtab__panel[role="tabpanel"]').forEach((panel) => {
      const tabButton = scopeRoot.querySelector(
        `.ctm-faqtab__tab[data-tab-key="${CSS.escape(panel.dataset.tabKey)}"]`
      );
      const tabLabel = tabButton?.querySelector('.ctm-faqtab__label')?.textContent.trim() || sourceLabel;

      panel.querySelectorAll('.ctm-faqtab__item').forEach((item) => {
        const title = item.querySelector('.ctm-faqtab__question')?.textContent.trim();
        if (!title) return;

        const answer = item.querySelector('.ctm-faqtab__content')?.textContent.trim() || '';
        entries.push({
          kind: 'tab-question',
          title,
          meta: tabLabel,
          element: item,
          panel,
          tabButton,
          searchable: this.normalizeText([sourceLabel, tabLabel, title, answer].join(' ')),
          order: entries.length
        });
      });
    });
  }

  performSearch() {
    this.clearTargetHighlights();

    const query = this.normalizeText(this.input.value);
    if (!query) {
      this.renderResults([], '');
      return;
    }

    const tokens = query.split(' ');
    const matches = this.entries
      .map((entry) => ({
        entry,
        score: this.scoreEntry(entry, query, tokens)
      }))
      .filter(({ score }) => score > 0)
      .sort(
        (a, b) =>
          b.score - a.score ||
          a.entry.title.localeCompare(b.entry.title) ||
          a.entry.order - b.entry.order
      )
      .map(({ entry }) => entry);

    this.renderResults(matches, query);
  }

  scoreEntry(entry, query, tokens) {
    const title = this.normalizeText(entry.title);
    const meta = this.normalizeText(entry.meta);
    let score = 0;

    if (title === query) score += 200;
    if (title.startsWith(query)) score += 120;
    if (title.includes(query)) score += 80;
    if (meta.includes(query)) score += 20;
    if (entry.searchable.includes(query)) score += 30;

    return score + tokens.filter((token) => entry.searchable.includes(token)).length * 8;
  }

  renderResults(entries, query) {
    this.resultsList.replaceChildren();
    this.visibleEntries = entries.slice(0, 8);
    this.lastQuery = query;

    if (!query) {
      this.a11y.setSearchExpanded(false);
      this.a11y.announceSearch('');
      return;
    }

    if (!this.visibleEntries.length) {
      const emptyItem = document.createElement('li');
      emptyItem.className = 'ctm-faqsrch_result-empty';
      emptyItem.textContent = 'No matching FAQs found';
      this.resultsList.append(emptyItem);
      this.a11y.setSearchExpanded(true);
      this.a11y.announceSearch('No matching FAQs found');
      return;
    }

    const terms = this.getHighlightTerms(query);
    const sectionId = this.dataset.ctmFaqSearchSection;

    this.visibleEntries.forEach((entry, index) => {
      const option = document.createElement('li');
      const meta = document.createElement('span');
      const title = document.createElement('span');

      option.className = 'ctm-faqsrch_result';
      this.a11y.configureSearchOption(option, index, `ctm-faqsrch-option-${sectionId}-${index}`);

      meta.className = 'ctm-faqsrch_result-meta';
      meta.textContent = entry.meta;
      title.className = 'ctm-faqsrch_result-title';
      title.append(this.createHighlightedFragment(entry.title, terms));
      option.append(meta, title);
      this.resultsList.append(option);
    });

    this.a11y.setSearchExpanded(true);
    this.a11y.setSearchActive(0);
    this.a11y.announceSearch(
      `${this.visibleEntries.length} FAQ ${this.visibleEntries.length === 1 ? 'result' : 'results'} available`
    );
  }

  activateEntry(entry) {
    const query = this.lastQuery;
    this.input.value = entry.title;
    this.a11y.setSearchExpanded(false);
    this.a11y.announceSearch('');
    this.highlightEntry(entry, query);

    if (entry.kind === 'tab-question') {
      const revealQuestion = () => {
        entry.panel.querySelectorAll('.ctm-faqtab__item[open]').forEach((item) => {
          if (item !== entry.element) item.open = false;
        });
        entry.element.open = true;
        this.scrollToTarget(entry.element.querySelector('.ctm-faqtab__summary'));
      };

      if (entry.panel.hidden) {
        entry.tabButton.click();
        requestAnimationFrame(() => requestAnimationFrame(revealQuestion));
      } else {
        revealQuestion();
      }
    } else if (!entry.group.classList.contains('is-open')) {
      entry.groupToggle.click();
      requestAnimationFrame(() => requestAnimationFrame(() => this.scrollToTarget(entry.element)));
    } else {
      this.scrollToTarget(entry.element);
    }

    this.a11y.focus(this.input, { preventScroll: true });
  }

  scrollToTarget(target) {
    const offset = this.getHeaderOffset() + this.searchBox.getBoundingClientRect().height + 20;
    const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
    window.scrollTo({ top, behavior: 'smooth' });
  }

  getHighlightTerms(query) {
    return Array.from(
      new Set(
        this.normalizeText(query)
          .split(' ')
          .filter((token) => token.length > 1 || /[\u4e00-\u9fff]/.test(token))
      )
    ).sort((a, b) => b.length - a.length);
  }

  createHighlightedFragment(text, terms, className = '') {
    const fragment = document.createDocumentFragment();
    if (!terms.length) {
      fragment.append(text);
      return fragment;
    }

    const pattern = new RegExp(`(${terms.map((term) => this.escapeRegExp(term)).join('|')})`, 'ig');
    let lastIndex = 0;

    for (const match of text.matchAll(pattern)) {
      fragment.append(text.slice(lastIndex, match.index));
      const mark = document.createElement('mark');
      mark.className = className;
      mark.textContent = match[0];
      fragment.append(mark);
      lastIndex = match.index + match[0].length;
    }

    fragment.append(text.slice(lastIndex));
    return fragment;
  }

  highlightEntry(entry, query) {
    this.clearTargetHighlights();
    const terms = this.getHighlightTerms(query);
    const selectors = entry.kind === 'tab-question'
      ? ['.ctm-faqtab__question', '.ctm-faqtab__content']
      : ['.ctm-faqgrid__card-title', '.ctm-faqgrid__card-text', '.ctm-faqgrid__card-link'];

    selectors.forEach((selector) => {
      const element = entry.element.querySelector(selector);
      if (element) this.highlightElement(element, terms);
    });
  }

  highlightElement(element, terms) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (terms.some((term) => this.normalizeText(node.nodeValue).includes(term))) {
        textNodes.push(node);
      }
    }

    textNodes.forEach((node) => {
      const fragment = this.createHighlightedFragment(node.nodeValue, terms, 'ctm-faqsrch-highlight');
      this.highlightMarks.push(...fragment.querySelectorAll('mark'));
      node.replaceWith(fragment);
    });
  }

  clearTargetHighlights() {
    this.highlightMarks.forEach((mark) => {
      if (!mark.isConnected) return;
      const parent = mark.parentNode;
      mark.replaceWith(...mark.childNodes);
      parent.normalize();
    });
    this.highlightMarks = [];
  }

  syncDockTop() {
    const top = this.getHeaderOffset();
    this.style.setProperty('--ctm-faqsrch-sticky-top', `${top}px`);
    return top;
  }

  updateDockState() {
    const dockTop = this.syncDockTop();

    if (!this.classList.contains('ctm-faqsrch--docked') || this.dockAnchorTop === null) {
      this.dockAnchorTop = this.form.getBoundingClientRect().top + window.scrollY;
    }

    const shouldDock = window.scrollY + dockTop >= this.dockAnchorTop;
    this.classList.toggle('ctm-faqsrch--docked', shouldDock);
    this.syncDockSpacer();
  }

  syncDockSpacer() {
    const isDocked = this.classList.contains('ctm-faqsrch--docked');
    this.spacer.hidden = !isDocked;
    this.spacer.style.height = isDocked
      ? `${Math.ceil(this.form.getBoundingClientRect().height)}px`
      : '0px';
  }

  requestDockUpdate() {
    if (this.dockFrame) return;

    this.dockFrame = requestAnimationFrame(() => {
      this.updateDockState();
      this.dockFrame = null;
    });
  }
}

customElements.define('ctm-faq-search', CtmFaqSearch);
/* CTM FAQ components end */

/* CTM scroll anchor start */
class CtmScrollAnchorA11y {
  focusTarget(target) {
    const requiresTemporaryTabIndex = !target.matches(
      'a[href], button, input, select, textarea, summary, [tabindex]'
    );

    if (requiresTemporaryTabIndex) {
      target.tabIndex = -1;
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }

    target.focus({ preventScroll: true });
  }
}

class CtmScrollAnchor extends HTMLElement {
  constructor() {
    super();
    this.a11y = new CtmScrollAnchorA11y();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.link = this.querySelector('[data-ctm-scroll-link]');
    this.link?.addEventListener('click', this, { signal: this.abortController.signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleEvent(event) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const targetId = this.link?.dataset.ctmScrollTarget;
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    event.preventDefault();
    this.updateHash(targetId);
    this.a11y.focusTarget(target);
    this.scrollToTarget(target);
  }

  getHeaderOffset() {
    const headerSection = document.querySelector('.header-section');
    if (headerSection?.classList.contains('header-hidden')) return 0;

    const header = headerSection?.querySelector('header[is="sticky-header"], header') ||
      document.querySelector('header[is="sticky-header"], header');
    const headerHeight = header ? Math.ceil(header.getBoundingClientRect().height) : 0;
    const rootStyles = getComputedStyle(document.documentElement);
    const stickyHeight = Number.parseFloat(rootStyles.getPropertyValue('--sticky-header-height')) || 0;

    return Math.max(headerHeight, stickyHeight);
  }

  scrollToTarget(target) {
    const top = Math.max(
      0,
      window.scrollY + target.getBoundingClientRect().top - this.getHeaderOffset()
    );
    const behavior = this.reduceMotion.matches ? 'auto' : 'smooth';

    window.scrollTo({ top, behavior });
  }

  updateHash(targetId) {
    const url = new URL(window.location.href);
    url.hash = targetId;

    if (url.href === window.location.href) {
      window.history.replaceState(window.history.state, '', url);
    } else {
      window.history.pushState(window.history.state, '', url);
    }
  }
}

if (!customElements.get('ctm-scroll-anchor')) {
  customElements.define('ctm-scroll-anchor', CtmScrollAnchor);
}
/* CTM scroll anchor end */

/* CTM swatch gallery start */
class CtmSwatchGalleryA11y {
  getFocusable(container) {
    return Array.from(container.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'));
  }

  setSlideInteractive(slide, enabled) {
    slide.setAttribute('aria-hidden', String(!enabled));
    this.getFocusable(slide).forEach((element) => {
      if (!enabled) {
        if (!element.hasAttribute('data-ctm-gallery-tabindex')) {
          element.setAttribute('data-ctm-gallery-tabindex', element.getAttribute('tabindex') || '');
        }
        element.tabIndex = -1;
        return;
      }
      if (!element.hasAttribute('data-ctm-gallery-tabindex')) return;
      const tabindex = element.getAttribute('data-ctm-gallery-tabindex');
      if (tabindex) {
        element.setAttribute('tabindex', tabindex);
      } else {
        element.removeAttribute('tabindex');
      }
      element.removeAttribute('data-ctm-gallery-tabindex');
    });
  }

  syncSlides(gallery) {
    const slides = Array.from(gallery.variants?.querySelectorAll('.ctm-swatch-gallery__variant') || []);
    if (!slides.length) return;
    const activeSlide = slides[gallery.variantIndex] || slides[0];
    slides.forEach((slide) => this.setSlideInteractive(slide, slide === activeSlide));
  }

  syncButtons(variants) {
    const previousButton = variants?.querySelector(':scope > .flickity-button.previous');
    const nextButton = variants?.querySelector(':scope > .flickity-button.next');
    const viewport = variants?.querySelector(':scope > .flickity-viewport');
    previousButton?.setAttribute('aria-label', 'Previous look');
    nextButton?.setAttribute('aria-label', 'Next look');
    if (nextButton && viewport && viewport.nextElementSibling !== nextButton) {
      viewport.after(nextButton);
    }
  }

  syncThumbnails(variant, selectedIndex = 0) {
    const thumbnails = Array.from(variant?.querySelectorAll('[data-gallery-image-index]') || []);
    if (!thumbnails.length) return;
    thumbnails.forEach((thumbnail) => {
      const isActive = Number(thumbnail.dataset.galleryImageIndex || 0) === selectedIndex;
      thumbnail.classList.toggle('is-selected', isActive);
      thumbnail.setAttribute('aria-current', String(isActive));
    });
  }

  refreshFocusTrap(modal) {
    if (!modal?.open || !theme?.a11y?.trapFocus) return;
    theme.a11y.trapFocus(modal, null);
  }
}

class CtmSwatchGalleryItem extends HTMLElement {
  constructor() {
    super();
    this.carousel = null;
    this.itemData = null;
    this.itemIndex = 0;
    this.isRendered = false;
    this.a11y = new CtmSwatchGalleryA11y();
  }

  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    if (!this.gallery || this.itemIndex === this.gallery.variantIndex) {
      this.ensureRendered();
    }
  }

  ensureRendered() {
    if (this.isRendered) return;
    this.render();
    this.initCarousel();
    this.bindThumbnails();
    this.isRendered = true;
  }

  disconnectedCallback() {
    this.carousel?.destroy();
    this.carousel = null;
    this.abortController?.abort();
    this.isRendered = false;
  }

  get gallery() {
    return this.closest('ctm-swatch-gallery');
  }

  render() {
    if (this.querySelector('[data-gallery-track]') || !this.itemData) return;

    const item = this.itemData;
    this.classList.add('ctm-swatch-gallery__variant');
    this.dataset.galleryVariantIndex = String(this.itemIndex);
    this.setAttribute('role', 'group');
    this.setAttribute('aria-roledescription', 'slide');
    this.setAttribute('aria-label', `Look ${this.itemIndex + 1}`);

    const layout = document.createElement('div');
    layout.className = 'ctm-swatch-gallery__layout';
    const thumbs = document.createElement('div');
    thumbs.className = 'ctm-swatch-gallery__thumbs ctm-vertical-scroll';
    thumbs.dataset.galleryThumbs = '';
    thumbs.setAttribute('role', 'group');
    thumbs.setAttribute('aria-label', 'Gallery thumbnails');
    const media = document.createElement('div');
    media.className = 'ctm-swatch-gallery__media';
    const track = document.createElement('div');
    track.className = 'ctm-swatch-gallery__track';
    track.dataset.galleryTrack = '';
    const counter = document.createElement('div');
    counter.className = 'ctm-swatch-gallery__counter';
    counter.setAttribute('aria-live', 'polite');
    counter.setAttribute('aria-atomic', 'true');
    const counterCurrent = document.createElement('span');
    counterCurrent.className = 'ctm-swatch-gallery__counter-current';
    counterCurrent.dataset.galleryCounter = '';
    counterCurrent.textContent = '1';
    const counterTotal = document.createElement('span');
    counterTotal.textContent = String(item.galleryImages.length);
    counter.append(counterCurrent, document.createTextNode(' / '), counterTotal);
    const details = document.createElement('div');
    details.className = 'ctm-swatch-gallery__details ctm-vertical-scroll';
    const swatches = document.createElement('div');
    swatches.className = 'ctm-swatch-gallery__swatches';
    swatches.dataset.gallerySwatches = '';

    media.append(track, counter);
    details.appendChild(swatches);
    layout.append(thumbs, media, details);
    this.appendChild(layout);
    this.renderImages(item.galleryImages);
    this.renderSwatches(item.swatches, swatches);
  }

  createFields(fields) {
    const list = document.createElement('ul');
    list.className = 'ctm-swatch-gallery__fields';
    fields.forEach((field) => {
      const [label, ...valueParts] = String(field).split('||');
      const value = valueParts.join('||');
      if (!label && !value) return;
      const row = document.createElement('li');
      row.className = 'ctm-swatch-gallery__field';
      const term = document.createElement('span');
      const description = document.createElement('span');
      term.textContent = `${label || 'Detail'}:`;
      description.textContent = value;
      row.append(term, document.createTextNode(' '), description);
      list.appendChild(row);
    });
    return list;
  }

  renderSwatches(swatches, container) {
    container.replaceChildren();
    swatches.forEach((swatch) => {
      const item = document.createElement('article');
      item.className = 'ctm-swatch-gallery__swatch';

      const title = document.createElement('div');
      title.className = 'ctm-swatch-gallery__swatch-title';
      title.textContent = swatch.title;
      item.appendChild(title);

      const info = document.createElement('div');
      info.className = 'ctm-swatch-gallery__swatch-info';
      if (swatch.image) {
        const image = document.createElement('img');
        image.src = swatch.image;
        image.alt = swatch.title;
        image.loading = 'lazy';
        image.width = 108;
        image.height = 108;
        info.appendChild(image);
      }
      info.appendChild(this.createFields(swatch.fields));
      item.appendChild(info);

      const actions = document.createElement('div');
      actions.className = 'ctm-swatch-gallery__swatch-actions';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ctm-swatch-gallery__add ctm-loading-btn';
      button.dataset.gallerySwatchAction = '';
      button.dataset.variantId = swatch.variantId;
      button.dataset.available = String(swatch.available);
      button.dataset.swatchTitle = swatch.title;
      button.disabled = true;
      const label = document.createElement('span');
      label.dataset.swatchButtonLabel = '';
      label.textContent = 'Add Swatch';
      button.appendChild(label);
      actions.appendChild(button);

      if (swatch.link) {
        const link = document.createElement('a');
        link.className = 'ctm-swatch-gallery__shop';
        link.href = swatch.link;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = 'Shop Now';
        link.setAttribute('aria-label', 'Shop this look');
        actions.appendChild(link);
      }
      item.appendChild(actions);
      container.appendChild(item);
    });
  }

  renderImages(images) {
    const track = this.querySelector('[data-gallery-track]');
    const thumbs = this.querySelector('[data-gallery-thumbs]');
    this.style.setProperty('--gallery-image-ratio', '1');

    images.forEach((image, index) => {
      const slide = document.createElement('div');
      slide.className = 'ctm-swatch-gallery__slide';
      const media = document.createElement('img');
      media.className = 'ctm-swatch-gallery__image';
      media.src = image.src;
      media.srcset = `${image.src} 560w, ${image.src960} 960w`;
      media.sizes = '(max-width: 767px) 120px, 800px';
      media.alt = image.alt;
      media.loading = index === 0 ? 'eager' : 'lazy';
      media.width = 960;
      media.height = 960;
      slide.appendChild(media);
      track.appendChild(slide);

      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = `ctm-swatch-gallery__thumb${index === 0 ? ' is-selected' : ''}`;
      thumbnail.dataset.galleryImageIndex = String(index);
      thumbnail.setAttribute('aria-label', `Show image ${index + 1} of ${images.length}`);
      thumbnail.setAttribute('aria-current', String(index === 0));
      const thumbnailImage = document.createElement('img');
      thumbnailImage.src = image.thumb;
      thumbnailImage.alt = image.alt || '';
      thumbnailImage.loading = 'lazy';
      thumbnailImage.width = 60;
      thumbnailImage.height = 60;
      thumbnail.appendChild(thumbnailImage);
      thumbs.appendChild(thumbnail);
    });

    this.syncImageState(0);
  }

  bindThumbnails() {
    this.querySelectorAll('[data-gallery-image-index]').forEach((thumbnail) => {
      thumbnail.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (thumbnail.classList.contains('is-selected')) return;
        const index = Number(thumbnail.dataset.galleryImageIndex || 0);
        this.a11y.syncThumbnails(this, index);
        this.carousel?.select(index);
        if (!this.carousel) this.syncImageState(index);
      }, { signal: this.abortController.signal });
    });
  }

  initCarousel() {
    if (this.carousel) {
      this.carousel.resize();
      return;
    }
    const track = this.querySelector('[data-gallery-track]');
    const items = track?.querySelectorAll('.ctm-swatch-gallery__slide');
    if (!track || typeof Flickity !== 'function' || !items || items.length <= 1) return;
    const isMobile = theme.config.mqlSmall;
    let options = { fade: true, pageDots: false, draggable: false };
    if (isMobile) {
      options = { fade: false, pageDots: true, draggable: true };
    }
    this.carousel = new Flickity(track, {
      cellSelector: '.ctm-swatch-gallery__slide',
      prevNextButtons: false,
      accessibility: false,
      ...options,
    });
    this.carousel.on('change', (index) => {
      this.syncImageState(index);
    });
    if (isMobile) {
      ['pointerdown', 'touchstart', 'mousedown'].forEach((eventName) => {
        track.addEventListener(eventName, (event) => event.stopPropagation(), { signal: this.abortController.signal });
      });
      let startX = 0;
      this.carousel.on('dragStart', (event, pointer) => {
        startX = pointer.clientX;
      });
      this.carousel.on('dragEnd', (event, pointer) => {
        const moveX = pointer.clientX - startX;
        const item = pointer.target.closest('.ctm-swatch-gallery__slide');
        const index = Array.from(items).indexOf(item) + 1;
        if (index === items.length && moveX < 0) {
          this.prevNextSide();
        }
        if (index === 1 && moveX > 0) {
          this.prevNextSide('prev');
        }
      });
    }
  }

  syncImageState(index) {
    const images = this.itemData?.galleryImages || [];
    const imageIndex = Math.max(0, Math.min(Number(index) || 0, Math.max(0, images.length - 1)));
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = String(imageIndex + 1);
    this.a11y.syncThumbnails(this, imageIndex);
    this.querySelectorAll('.ctm-swatch-gallery__slide').forEach((slide, slideIndex) => {
      slide.setAttribute('aria-hidden', String(slideIndex !== imageIndex));
    });
  }

  prevNextSide(type = 'next') {
    const carousel = this.gallery?.variantCarousel;
    if (!carousel) return;
    if (this.contains(document.activeElement)) {
      document.activeElement.blur();
    }
    const { selectedIndex, slides } = carousel;
    if (type === 'next' && selectedIndex + 1 < slides.length) {
      carousel.select(selectedIndex + 1);
    }
    if (type === 'prev' && selectedIndex > 0) {
      carousel.select(selectedIndex - 1);
    }
  }
}

class CtmSwatchGallery extends HTMLElement {
  constructor() {
    super();
    this.a11y = new CtmSwatchGalleryA11y();
    this.items = [];
    this.variantIndex = 0;
    this.variantCarousel = null;
    this.trigger = null;
    this.owner = null;
    this.ownerType = '';
    this.cartSwatchCount = 0;
    this.cartSwatchIds = new Set();
    this.cartStateReady = false;
    this.cartRequestId = 0;
    this.actionRequestId = 0;
    this.actionPending = false;
    this.handleClick = this.handleClick.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleModalAfterHide = this.handleModalAfterHide.bind(this);
    this.handleModalAfterShow = this.handleModalAfterShow.bind(this);
  }

  connectedCallback() {
    this.modal = this.closest('x-modal');
    this.variants = this.querySelector('[data-gallery-variants]');

    this.addEventListener('click', this.handleClick);
    this.modal?.addEventListener('modal:afterHide', this.handleModalAfterHide);
    this.modal?.addEventListener('modal:afterShow', this.handleModalAfterShow);
    document.addEventListener('cart:updated', this.handleCartUpdate);
    if (theme?.pubsub?.subscribe && theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.handleCartUpdate
      );
    }
  }

  disconnectedCallback() {
    if (this.items.length) this.cleanup();
    this.removeEventListener('click', this.handleClick);
    this.modal?.removeEventListener('modal:afterHide', this.handleModalAfterHide);
    this.modal?.removeEventListener('modal:afterShow', this.handleModalAfterShow);
    document.removeEventListener('cart:updated', this.handleCartUpdate);
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = null;
  }

  get isOpen() {
    return Boolean(this.modal?.open);
  }

  open({ items = [], activeVariantId = null, trigger = null, owner = null, ownerType = '' } = {}) {
    const normalizedItems = items.map((item) => this.normalizeItem(item)).filter((item) => item.galleryImages.length);
    if (!normalizedItems.length) return false;

    this.actionRequestId += 1;
    this.actionPending = false;
    this.items = normalizedItems;
    this.variantIndex = Math.max(0, normalizedItems.findIndex((item) => String(item.variantId) === String(activeVariantId)));
    this.trigger = trigger instanceof HTMLElement ? trigger : document.activeElement;
    this.owner = owner instanceof HTMLElement ? owner : trigger?.closest?.('ctm-fabric-swatches, ctm-swatch-library, custom-product-template, custom-product-bundled');
    this.ownerType = ownerType || this.owner?.localName || '';
    this.cartStateReady = false;
    this.renderVariants();
    this.refreshCartState().catch((error) => this.showCartStateError(error));
    this.modal?.show(this.trigger);
    requestAnimationFrame(() => {
      this.resizeCarousels();
    });

    return true;
  }

  close() {
    this.modal?.hide();
  }

  handleModalAfterHide() {
    this.cleanup();
  }

  handleModalAfterShow() {
    if (!this.isOpen || !this.items.length) return;
    this.resizeCarousels();
    this.a11y.syncSlides(this);
    this.a11y.syncButtons(this.variants);
  }

  cleanup() {
    this.destroyCarousels();
    this.actionRequestId += 1;
    this.cartRequestId += 1;
    this.actionPending = false;
    this.trigger = null;
    this.owner = null;
    this.ownerType = '';
    this.items = [];
    this.variants?.replaceChildren();
  }

  resizeCarousels() {
    this.variantCarousel?.resize();
    this.querySelectorAll('ctm-swatch-gallery-item').forEach((item) => item.carousel?.resize());
  }

  normalizeItem(item = {}) {
    const rawImages = item.galleryImages || item.fabric_gallery || [];
    const galleryImages = Array.isArray(rawImages)
      ? rawImages.map((image) => this.normalizeImage(image)).filter((image) => image.src)
      : [];

    return {
      productHandle: item.productHandle || item.product_handle || '',
      productTitle: item.productTitle || item.product_title || '',
      productUrl: item.productUrl || item.product_url || '',
      variantId: item.variantId || item.variant_id || '',
      variantTitle: item.variantTitle || item.title || '',
      sku: item.sku || '',
      available: item.available !== false && item.available !== 'false',
      swatchImage: item.swatchImage || item.image || '',
      fields: Array.isArray(item.fields)
        ? item.fields
        : (Array.isArray(item.product_gallery_swatch_fields) ? item.product_gallery_swatch_fields : []),
      swatches: this.normalizeSwatches(item),
      galleryImages,
    };
  }

  normalizeSwatches(item = {}) {
    const hasExplicitSwatches = Object.prototype.hasOwnProperty.call(item, 'swatches');
    const rawSwatches = hasExplicitSwatches && Array.isArray(item.swatches)
      ? item.swatches
      : [{
        variantId: item.variantId || item.variant_id,
        title: item.variantTitle || item.title,
        available: item.available,
        image: item.swatchImage || item.image,
        fields: item.fields || item.product_gallery_swatch_fields,
        link: item.link || item.product_gallery_swatch_link,
      }];

    return rawSwatches
      .map((swatch) => ({
        variantId: swatch.variantId || swatch.variant_id || swatch.id || '',
        title: swatch.title || swatch.variantTitle || '',
        available: swatch.available !== false && swatch.available !== 'false',
        image: typeof swatch.image === 'string' ? swatch.image : (swatch.image?.src || ''),
        fields: Array.isArray(swatch.fields)
          ? swatch.fields
          : (Array.isArray(swatch.product_gallery_swatch_fields) ? swatch.product_gallery_swatch_fields : []),
        link: swatch.link || swatch.product_gallery_swatch_link || '',
      }))
      .filter((swatch) => swatch.variantId);
  }

  normalizeImage(image = {}) {
    if (typeof image === 'string') {
      return { src: image, src960: image, thumb: image, alt: '', width: '', height: '' };
    }

    return {
      src: image.src || image.url || image.preview_image?.src || '',
      src960: image.src960 || image.src_960 || image.src || image.url || image.preview_image?.src || '',
      thumb: image.thumb || image.src || image.url || image.preview_image?.src || '',
      alt: image.alt || '',
      width: image.width || '',
      height: image.height || '',
    };
  }

  renderVariants() {
    this.destroyCarousels();
    this.variants.replaceChildren();

    this.items.forEach((item, itemIndex) => {
      const cell = document.createElement('ctm-swatch-gallery-item');
      cell.className = 'ctm-swatch-gallery__variant';
      cell.dataset.galleryVariantIndex = String(itemIndex);
      cell.setAttribute('role', 'group');
      cell.setAttribute('aria-roledescription', 'slide');
      cell.setAttribute('aria-label', `Look ${itemIndex + 1}`);
      cell.itemData = item;
      cell.itemIndex = itemIndex;
      this.variants.appendChild(cell);
    });

    if (typeof Flickity === 'function' && this.items.length > 1) {
      this.variantCarousel = new Flickity(this.variants, {
        cellSelector: 'ctm-swatch-gallery-item',
        cellAlign: 'left',
        groupCells: false,
        contain: true,
        prevNextButtons: true,
        pageDots: false,
        dragThreshold: theme.config.mqlSmall ? 40 : 3,
        initialIndex: this.variantIndex,
      });
      this.variantCarousel.on('change', (index) => this.syncVariantState(index));
    }
    this.syncVariantState(this.variantIndex);
  }

  syncVariantState(index) {
    this.variantIndex = Math.max(0, Math.min(Number(index) || 0, Math.max(0, this.items.length - 1)));
    const activeItem = this.querySelector(`ctm-swatch-gallery-item[data-gallery-variant-index="${this.variantIndex}"]`);
    activeItem?.ensureRendered();
    this.a11y.syncSlides(this);
    this.a11y.syncButtons(this.variants);
    this.syncSwatchButtons();
    activeItem?.carousel?.resize();
  }

  getCartStateUrl() {
    return `${window.Shopify?.routes?.root || '/'}cart?sections=cart-swatch-count`;
  }

  parseCartState(sections = {}) {
    const sectionHTML = sections?.['cart-swatch-count'];
    if (!sectionHTML) throw new Error('Swatch cart state is unavailable. Check the Single Swatch product tag configuration.');
    const state = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector('[data-num][data-ids]');
    if (!state) throw new Error('Swatch cart state is invalid. Check the Single Swatch product tag configuration.');
    const count = Number(state.dataset.num);
    if (!Number.isFinite(count)) throw new Error('Swatch cart count is invalid.');
    return {
      count,
      ids: new Set(String(state.dataset.ids || '').split(',').filter(Boolean)),
    };
  }

  applyCartState(sections) {
    const state = this.parseCartState(sections);
    this.cartSwatchCount = state.count;
    this.cartSwatchIds = state.ids;
    this.cartStateReady = true;
    this.syncSwatchButtons();
    return state;
  }

  async refreshCartState(sections = null) {
    const requestId = ++this.cartRequestId;
    if (sections?.['cart-swatch-count']) {
      if (requestId !== this.cartRequestId || !this.isOpen) return null;
      return this.applyCartState(sections);
    }
    const response = await fetch(this.getCartStateUrl());
    if (!response.ok) throw new Error(`Swatch cart state request failed: ${response.status}`);
    const parsedSections = await response.json();
    if (requestId !== this.cartRequestId || !this.isOpen) return null;
    return this.applyCartState(parsedSections);
  }

  handleCartUpdate(event = {}) {
    if (!this.isOpen) return;
    const sections = event.cart?.sections || event.detail?.cart?.sections;
    this.refreshCartState(sections).catch((error) => this.showCartStateError(error));
  }

  showCartStateError() {
    if (!this.isOpen) return;
    this.cartStateReady = false;
    this.syncSwatchButtons();
  }

  syncSwatchButtons({ includeBusy = false } = {}) {
    const max = Number(theme?.ctm?.cartMaxSwatchItems) || 5;
    const limitReached = this.cartSwatchCount >= max;
    this.querySelectorAll('[data-gallery-swatch-action]').forEach((button) => {
      if (!includeBusy && button.getAttribute('aria-busy') === 'true') return;
      const variantId = String(button.dataset.variantId || '');
      const available = button.dataset.available !== 'false';
      const inCart = this.cartSwatchIds.has(variantId);
      const title = button.dataset.swatchTitle || 'swatch';
      const label = button.querySelector('[data-swatch-button-label]');
      button.classList.toggle('is-in-cart', inCart);
      button.classList.toggle('is-limit-reached', this.cartStateReady && limitReached && !inCart && available);

      if (!this.cartStateReady) {
        button.disabled = true;
        button.setAttribute('aria-pressed', 'false');
        if (label) label.textContent = 'Add Swatch';
      } else if (inCart) {
        button.disabled = false;
        button.setAttribute('aria-pressed', 'true');
        button.setAttribute('aria-label', `Remove swatch ${title} from cart`);
        if (label) label.textContent = 'In Cart';
      } else if (!available) {
        button.disabled = true;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', `${title} sold out`);
        if (label) label.textContent = 'Sold Out';
      } else if (limitReached) {
        button.disabled = true;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', 'Swatch limit reached');
        if (label) label.textContent = 'Add Swatch';
      } else {
        button.disabled = false;
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('aria-label', `Add swatch ${title}`);
        if (label) label.textContent = 'Add Swatch';
      }

      if (this.actionPending) button.disabled = true;
    });
    this.a11y.refreshFocusTrap(this.modal);
  }

  async toggleSwatch(button) {
    if (!this.owner || !this.cartStateReady || this.actionPending || button.disabled) return;
    const requestId = ++this.actionRequestId;
    const variantId = String(button.dataset.variantId || '');
    const wasInCart = this.cartSwatchIds.has(variantId);
    const itemIndex = Number(button.closest('[data-gallery-variant-index]')?.dataset.galleryVariantIndex || 0);
    const detail = {
      button,
      item: this.items[itemIndex],
      variantId,
      action: wasInCart ? 'remove' : 'add',
      promise: null,
    };

    this.actionPending = true;
    button.disabled = true;
    this.loadingSwatchButton('add', button);
    this.syncSwatchButtons();

    try {
      this.owner.dispatchEvent(new CustomEvent('ctm:swatch-gallery-toggle', { detail }));
      if (!detail.promise || typeof detail.promise.then !== 'function') {
        throw new Error(`No Gallery cart adapter is registered for ${this.ownerType || 'this source'}.`);
      }
      await detail.promise;
      if (requestId !== this.actionRequestId || !this.isOpen) return;
      const state = await this.refreshCartState();
      if (!state || requestId !== this.actionRequestId || !this.isOpen) return;
      const isInCart = state.ids.has(variantId);
      if (isInCart === wasInCart) {
        throw new Error(`Swatch ${wasInCart ? 'removal' : 'addition'} could not be confirmed.`);
      }
    } catch (error) {
      if (requestId !== this.actionRequestId || !this.isOpen) return;
      this.showCartStateError(error);
      console.error('[ctm-swatch-gallery] swatch update failed', error);
    } finally {
      if (requestId !== this.actionRequestId || !this.isOpen) {
        this.loadingSwatchButton('remove', button);
        return;
      }
      this.actionPending = false;
      this.syncSwatchButtons({ includeBusy: true });
      this.loadingSwatchButton('remove', button);
      if (!button.disabled) requestAnimationFrame(() => button.focus());
    }
  }

  loadingSwatchButton(state, button) {
    if (state === 'remove') {
      button.removeAttribute('aria-busy');
      button.querySelector('.ctm-btn-loader')?.remove();
      return;
    }
    button.setAttribute('aria-busy', 'true');
    button.querySelector('.ctm-btn-loader')?.remove();
    button.insertAdjacentHTML('beforeend', `
      <span class="ctm-btn-loader" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `);
  }

  destroyCarousels() {
    this.variantCarousel?.destroy?.();
    this.variantCarousel = null;
  }

  handleClick(event) {
    const addButton = event.target.closest('[data-gallery-swatch-action]');
    if (!addButton) return;
    event.preventDefault();
    event.stopPropagation();
    this.toggleSwatch(addButton);
  }

}

if (!customElements.get('ctm-swatch-gallery-item')) {
  customElements.define('ctm-swatch-gallery-item', CtmSwatchGalleryItem);
}

if (!customElements.get('ctm-swatch-gallery')) {
  customElements.define('ctm-swatch-gallery', CtmSwatchGallery);
}
/* CTM swatch gallery end */

/* Image with text hover / swap start */
class ImageWithTextMedia extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get slider() {
    return this.querySelector('slider-element');
  }

  get tabs() {
    return this._tabs = this._tabs || Array.from(this.querySelectorAll('.image-with-text__tab'));
  }

  get slides() {
    return this._slides = this._slides || Array.from(this.querySelectorAll('.image-with-text__slide'));
  }

  get liveRegion() {
    return this.querySelector('[data-iwt-live]');
  }

  get mode() {
    return this.getAttribute('data-mode') || 'none';
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.onChange = this.onChange.bind(this);
    this.onTabKey = this.onTabKey.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);

    if (this.slider) {
      this.slider.addEventListener('slider:change', this.onChange);
    }

    this.querySelector('[role="tablist"]')?.addEventListener('keydown', this.onTabKey);
    this.desktopQuery = window.matchMedia('screen and (min-width: 1024px)');
    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onBreakpoint);
    } else {
      this.desktopQuery.addListener(this.onBreakpoint);
    }
    this.onBreakpoint();
  }

  onBreakpoint() {
    const isDesktop = this.desktopQuery.matches;

    this.resetToFirstPage();

    if (isDesktop) {
      if (this.classList.contains('image-with-text__swap--hover')) {
        this.setAttribute('tabindex', '0');
      }
      return;
    }

    if (this.mode === 'tab') {
      this.removeAttribute('tabindex');
    }
  }

  resetToFirstPage() {
    if (!this.slider) return;

    this.slider.scrollTo({ left: 0, behavior: 'instant' });
    this.slider.currentPage = 1;
    this.syncPage(1);
  }

  syncPage(page) {
    this.slides.forEach((slide, index) => {
      const current = index + 1 === page;
      slide.classList.toggle('selected', current);
      slide.setAttribute('aria-hidden', current ? 'false' : 'true');
    });

    this.tabs.forEach((tab) => {
      const selected = parseInt(tab.getAttribute('data-index'), 10) === page;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('aria-current', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    if (this.liveRegion && this.mode === 'swipe') {
      const label = this.slides[page - 1]?.dataset.label || `Image ${page}`;
      this.liveRegion.textContent = `${label}, ${page} of ${this.slides.length}`;
    }
  }

  onChange(event) {
    this.syncPage(event.detail.currentPage);
  }

  onTabKey(event) {
    if (this.mode !== 'tab' || !this.slider) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') return;

    event.preventDefault();
    const rtl = Boolean(window.theme?.config?.rtl);
    let next = this.slider.currentPage || 1;

    if (event.key === 'Home') {
      next = 1;
    } else if (event.key === 'End') {
      next = this.slides.length;
    } else {
      const dir = ((event.key === 'ArrowRight') !== rtl) ? 1 : -1;
      next = Math.min(Math.max(next + dir, 1), this.slides.length);
    }

    this.slider.select(next);
    this.tabs[next - 1]?.focus();
  }
}

if (!customElements.get('image-with-text-media')) {
  customElements.define('image-with-text-media', ImageWithTextMedia);
}
/* Image with text hover / swap end */

/* Multicolumn hover / swap start */
class MulticolumnMedia extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get slider() {
    return this.querySelector('slider-element');
  }

  get tabs() {
    return this._tabs = this._tabs || Array.from(this.querySelectorAll('.multicolumn__tab'));
  }

  get slides() {
    return this._slides = this._slides || Array.from(this.querySelectorAll('.multicolumn__slide'));
  }

  get liveRegion() {
    return this.querySelector('[data-mcol-live]');
  }

  get mode() {
    return this.getAttribute('data-mode') || 'none';
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.onChange = this.onChange.bind(this);
    this.onTabKey = this.onTabKey.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);

    if (this.slider) {
      this.slider.addEventListener('slider:change', this.onChange);
    }

    this.querySelector('[role="tablist"]')?.addEventListener('keydown', this.onTabKey);
    this.desktopQuery = window.matchMedia('screen and (min-width: 1024px)');
    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.onBreakpoint);
    } else {
      this.desktopQuery.addListener(this.onBreakpoint);
    }
    this.onBreakpoint();
  }

  onBreakpoint() {
    const isDesktop = this.desktopQuery.matches;

    this.resetToFirstPage();

    if (isDesktop) {
      if (this.classList.contains('multicolumn__swap--hover')) {
        this.setAttribute('tabindex', '0');
      }
      return;
    }

    if (this.mode === 'tab') {
      this.removeAttribute('tabindex');
    }
  }

  resetToFirstPage() {
    if (!this.slider) return;

    this.slider.scrollTo({ left: 0, behavior: 'instant' });
    this.slider.currentPage = 1;
    this.syncPage(1);
  }

  syncPage(page) {
    this.slides.forEach((slide, index) => {
      const current = index + 1 === page;
      slide.classList.toggle('selected', current);
      slide.setAttribute('aria-hidden', current ? 'false' : 'true');
    });

    this.tabs.forEach((tab) => {
      const selected = parseInt(tab.getAttribute('data-index'), 10) === page;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('aria-current', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    if (this.liveRegion && this.mode === 'swipe') {
      const label = this.slides[page - 1]?.dataset.label || `Image ${page}`;
      this.liveRegion.textContent = `${label}, ${page} of ${this.slides.length}`;
    }
  }

  onChange(event) {
    this.syncPage(event.detail.currentPage);
  }

  onTabKey(event) {
    if (this.mode !== 'tab' || !this.slider) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End') return;

    event.preventDefault();
    const rtl = Boolean(window.theme?.config?.rtl);
    let next = this.slider.currentPage || 1;

    if (event.key === 'Home') {
      next = 1;
    } else if (event.key === 'End') {
      next = this.slides.length;
    } else {
      const dir = ((event.key === 'ArrowRight') !== rtl) ? 1 : -1;
      next = Math.min(Math.max(next + dir, 1), this.slides.length);
    }

    this.slider.select(next);
    this.tabs[next - 1]?.focus();
  }
}

if (!customElements.get('multicolumn-media')) {
  customElements.define('multicolumn-media', MulticolumnMedia);
}
/* Multicolumn hover / swap end */
